import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RotateCcw, Eye, Edit } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  requested: { label: 'Solicitada', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  approved:  { label: 'Aprobada',   className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  rejected:  { label: 'Rechazada',  className: 'bg-red-500/15 text-red-300 border-red-500/30' },
  completed: { label: 'Completada', className: 'bg-green-500/15 text-green-300 border-green-500/30' },
};

const REASON_LABELS = {
  producto_danado: 'Producto dañado',
  producto_incorrecto: 'Producto incorrecto',
  no_cumple_expectativas: 'No cumple expectativas',
  otro: 'Otro',
};

export default function Returns() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ status: 'requested', admin_notes: '', envia_return_tracking: '' });
  const [saving, setSaving] = useState(false);

  const fetchReturns = () => {
    setLoading(true);
    axios.get(`${API}/admin/returns`, { headers })
      .then(r => setReturns(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Error cargando devoluciones'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchReturns(); }, [token]);

  const openEdit = (ret) => {
    setEditItem(ret);
    setForm({
      status: ret.status,
      admin_notes: ret.admin_notes || '',
      envia_return_tracking: ret.envia_return_tracking || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/returns/${editItem.id}`, form, { headers });
      setReturns(prev => prev.map(r => r.id === editItem.id ? res.data : r));
      toast.success('Devolución actualizada');
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <RotateCcw className="h-5 w-5" /> Devoluciones
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestiona las solicitudes de devolución de los clientes</p>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Cargando...</CardContent></Card>
      ) : returns.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <RotateCcw className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No hay solicitudes de devolución registradas</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table data-testid="admin-returns-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returns.map(ret => {
                  const meta = STATUS_META[ret.status];
                  return (
                    <TableRow key={ret.id} data-testid={`return-row-${ret.order_number}`}>
                      <TableCell className="font-medium">{ret.order_number}</TableCell>
                      <TableCell>
                        <div className="text-sm">{ret.user_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{ret.user_email}</div>
                      </TableCell>
                      <TableCell className="text-sm">{REASON_LABELS[ret.reason] || ret.reason}</TableCell>
                      <TableCell>
                        {meta && <Badge variant="outline" className={meta.className} data-testid={`return-row-status-${ret.order_number}`}>{meta.label}</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(ret.created_at).toLocaleDateString('es-MX')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Link to={`/admin/ordenes/${ret.order_id}`}>
                            <Button variant="ghost" size="sm" title="Ver pedido"><Eye className="h-4 w-4" /></Button>
                          </Link>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(ret)} data-testid={`edit-return-${ret.order_number}`} title="Editar">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="edit-return-dialog">
          <DialogHeader>
            <DialogTitle>Editar Devolución</DialogTitle>
            <DialogDescription>
              {editItem ? `Pedido ${editItem.order_number} · ${editItem.user_email}` : ''}
            </DialogDescription>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="bg-secondary/40 rounded-md p-3 text-xs space-y-1">
                <div><span className="text-muted-foreground">Motivo cliente:</span> {REASON_LABELS[editItem.reason] || editItem.reason}</div>
                {editItem.comments && <div><span className="text-muted-foreground">Comentarios:</span> {editItem.comments}</div>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ret-status">Estado *</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger id="ret-status" data-testid="return-edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="requested">Solicitada</SelectItem>
                    <SelectItem value="approved">Aprobada</SelectItem>
                    <SelectItem value="rejected">Rechazada</SelectItem>
                    <SelectItem value="completed">Completada</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">
                  Al guardar con un nuevo estado se enviará un email automático al cliente (approved/rejected/completed).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ret-notes">Notas para el cliente</Label>
                <textarea
                  id="ret-notes"
                  value={form.admin_notes}
                  onChange={e => setForm({ ...form, admin_notes: e.target.value })}
                  rows={4}
                  maxLength={1000}
                  placeholder="Instrucciones, motivo de rechazo, etc."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="return-edit-notes-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ret-tracking">Guía de devolución (Envia.com)</Label>
                <Input
                  id="ret-tracking"
                  value={form.envia_return_tracking}
                  onChange={e => setForm({ ...form, envia_return_tracking: e.target.value })}
                  placeholder="Número de rastreo de la guía de retorno"
                  data-testid="return-edit-tracking-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="return-edit-save-button">
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
